// Drawer.jsx
// Client: vmfive
// Version: 1.1.0
// Right-side slide-over panel with scrim, Esc / scrim-click close.
// Uses CSS class toggling for 250ms enter/exit transitions.
//
// S-1 fix: double rAF ensures the transition fires even in React concurrent mode
//   (single rAF can be batched with the paint that applied mounted=true, causing
//   the browser to skip the from-state and jump directly to the end state).
// S-2 fix: body scroll lock while open, restoring the prior overflow value so
//   concurrent Modals are not clobbered.

import React, { useState, useEffect } from 'react';
import Icon from './Icon';
import './components-company-application.css';

/**
 * Drawer — props:
 *   open      {boolean}    Controls visibility
 *   onClose   {Function}   Called when user closes (Esc, scrim click, close button)
 *   eyebrow   {string=}    Small uppercase label above the title
 *   title     {string=}    Main title (18px/700)
 *   sub       {string=}    Subtitle / meta line below title (mono 12px)
 *   footer    {ReactNode=} Content rendered in the drawer footer bar
 *   children  {ReactNode}  Content rendered in the drawer body (scrollable)
 *   className {string=}    Extra CSS class on the .drawer panel
 *
 * Structure:
 *   .drawer-scrim   — full-viewport click-to-close overlay
 *   aside.drawer    — the panel itself
 *     .drawer__head   — eyebrow / title / sub / close button
 *     .drawer__body   — scrollable main content (children)
 *     .drawer__foot   — footer slot (optional)
 */
export default function Drawer({
  open,
  onClose,
  eyebrow,
  title,
  sub,
  footer,
  children,
  className = '',
}) {
  // mounted: keeps DOM present during exit animation
  // show: toggles .show class to trigger CSS transition
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);

  // S-1: double rAF so React concurrent mode does not skip the transition by
  // collapsing the initial (translateX:100%) and final (translateX:0) frames.
  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setShow(true));
      });
    } else {
      setShow(false);
      const timer = setTimeout(() => setMounted(false), 250);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Esc key handler
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // S-2: body scroll lock — save the prior overflow value and restore on close.
  // This ensures we do not clobber a Modal's lock (Modal also sets body overflow).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!mounted) return null;

  return (
    <>
      {/* Scrim */}
      <div
        className={`drawer-scrim${show ? ' show' : ''}`}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        className={`drawer${show ? ' show' : ''} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Panel'}
      >
        {/* Header */}
        {(eyebrow || title || sub || onClose) && (
          <div className="drawer__head">
            <div className="drawer__head-info">
              {eyebrow && <div className="drawer__eyebrow">{eyebrow}</div>}
              {title && <div className="drawer__title">{title}</div>}
              {sub && <div className="drawer__sub">{sub}</div>}
            </div>
            {onClose && (
              <button
                type="button"
                className="drawer__close"
                aria-label="Close panel"
                onClick={onClose}
              >
                <Icon name="x" size={18} strokeWidth={1.8} />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="drawer__body">{children}</div>

        {/* Footer */}
        {footer && <div className="drawer__foot">{footer}</div>}
      </aside>
    </>
  );
}
